import * as React from 'react';
import { AxiosResponse } from 'axios';
import './checklist.scss';
import AdditionalCompletionModal from 'components/checklist/additional-completion-modal/additional-completion-modal';
import WithSideBarAndNavBar from 'layouts/withSideBarAndNavBar/withSideBarAndNavBar';
import Button from 'common/button/button';
import CalendarInput from 'common/calendar-input/calendar-input';
import AddChecklistModal from 'components/checklist/add-checklist-modal/add-checklist-modal';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Popup } from 'semantic-ui-react';

// Assets
import { ReactComponent as TaskTickIcon } from 'assets/icons/icon_task_tick.svg';
import { ReactComponent as TaskTickRedIcon } from 'assets/icons/icon_task_tick_red.svg';
import { ReactComponent as LeftArrow } from 'assets/icons/left-arrow.svg';
import { ReactComponent as RightArrow } from 'assets/icons/right-arrow.svg';
import { ReactComponent as PinSolidIcon } from 'assets/icons/pin_solid_new.svg';
import { ReactComponent as PinIcon } from 'assets/icons/pin.svg';
import { ReactComponent as ActionIcon } from 'assets/icons/action.svg';

// Data
import Checklist from 'dummy-data/checklist.json';
import DateSelector from 'common/date-selector/date-selector';
import NavButton from 'common/nav-button/nav-button';
import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
import moment from 'moment';
import { IChecklist } from 'types/checklist';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';
import useStoreStore from 'zustands/useStoreStore';

const CheckList: React.FC = () => {
  const navigate = useNavigate();

  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();
  const { storeSeqNumber } = useStoreStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [date, setDate] = React.useState<Date>(new Date());
  const [checklistData, setChecklistData] = React.useState<IChecklist[]>([]);

  const [addChecklistModal, setAddChecklistModal] = React.useState<boolean>(false);
  const [additionalCompletionModal, setAdditionalCompletionModal] = React.useState<boolean>(false);
  const [workList, setWorkList] = React.useState<any>([]);

  // 체크리스트 데이터 패칭
  const fetchChecklist = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getcheckList`, {
        DATE: moment(date).format('YYYY-MM-DD'),
      });
      console.log('/auth/getcheckList_data', data);

      if (data.resultCode === 0) {
        setChecklistData(
          data.resultData.map((i: IChecklist) => ({
            CHECK_DATE: i.CHECK_DATE, // 체크한 날짜
            CHECK_LIST: i.CHECK_LIST, // "체크항목명@{정상:1, 이상:2}" => "싱크대@2@모서리@1"
            CHECK_SEQ: i.CHECK_SEQ, // 체크한 아이템 시퀀스
            CHECK_TIME: i.CHECK_TIME, // 체크한 시간
            CHECK_TITLE: i.CHECK_TITLE, // 체크할때 메모
            CS_SEQ: i.CS_SEQ, // 체크리스트 시퀀스
            EMP_NAME: i.EMP_NAME, // 체크한 직원명
            EMP_NAME2: i.EMP_NAME2, // 담당직원명
            EMP_SEQ: i.EMP_SEQ, // 담당직원시퀀스
            END_TIME: i.END_TIME, // 체크예정시간
            IMAGE_LIST: i.IMAGE_LIST, // 체크할때 업로드한 이미지
            LIST: i.LIST, // 체크항목
            MEMBER_SEQ1: i.MEMBER_SEQ1, // 체크한직원 시퀀스
            PHOTO_CHECK: i.PHOTO_CHECK, // 사진포함 여부
            TITLE: i.TITLE, //  체크리스트 제목
            UPDATE_TIME: i.UPDATE_TIME, // 체크리스트 최종체크시간
            regdate: i.regdate, // 체크리스트 등록시간
          })),
        );
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/getcheckList');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 이전일 또는 내일 변경
  const handleDateChange = (arrow: string) => {
    if (arrow === 'left') {
      handleSelectDate(moment(date).subtract(1, 'day').toDate());
    }
    if (arrow === 'right') {
      handleSelectDate(moment(date).add(1, 'day').toDate());
    }
  };

  // 날짜 데이터 변경
  const handleSelectDate = (date: Date) => {
    setDate(date);
  };

  // 양식 선택 모달 표시 토글
  // const handleToggleChecklistModal = () => {
  //   setAddChecklistModal(!addChecklistModal);
  // };

  // 추가하기 버튼 클릭
  const enterUpdateChecklist = (item?: IChecklist) => {
    navigate(
      `/add-checklist-form`,
      item
        ? {
            state: {
              checkItemData: item,
            },
          }
        : {},
    );
  };

  // 체크리스트 삭제 요청
  const deleteChecklistItem = async (item: IChecklist) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const list = item.LIST.split('@');
      const emps = item.EMP_SEQ?.split('@').map((i) => ({ EMP_SEQ: i }));
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/checkUpdate`, {
        STORE_SEQ: storeSeqNumber,
        CHECK_SEQ: item.CHECK_SEQ,
        TITLE: item.TITLE,
        LIST: list,
        CHECK_EMP_FLAG: item.EMP_SEQ ? '1' : '0',
        EMP_SEQS: emps ?? null,
        END_TIME: item.END_TIME,
        PHOTO_CHECK: item.PHOTO_CHECK,
        CLOSE_FLAG: '1',
      });
      console.log('/auth/checkUpdate_data', data);

      if (data.resultCode === 0) {
        fetchChecklist();
        setModalState({
          isModalShow: true,
          title: '체크리스트 삭제 완료',
          desc: '',
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/checkUpdate');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 체크리스트 아이템 메뉴 드롭다운
  const cardDropdown = (action: string, item: IChecklist) => {
    if (action === 'ACCEPT') {
      enterUpdateChecklist(item);
    }

    if (action === 'REJECT') {
      setModalState({
        isModalShow: true,
        title: '체크리스트 삭제',
        desc: '해당 체크리스트를 삭제합니다',
        okBtnText: '삭제',
        okCallback: () => deleteChecklistItem(item),
        isWarning: true,
      });
    }
  };

  const handleAdditionalCompletionModal = () => {
    setAdditionalCompletionModal(true);
  };

  const navigateToChecklistItem = (item: IChecklist) => {
    navigate(`/checklist-item`, {
      state: { item },
    });
  };

  const handlePinClick = (work: any) => {
    const workListData = workList.map((data: any) => {
      if (data.id === work.id) {
        return { ...data, pinned: !data.pinned };
      }
      return { ...data };
    });
    setWorkList(workListData);
    if (work.pinned) {
      toast(
        <div className="checklist-toast">
          <TaskTickRedIcon />
          <span className="checklist-toast__text">체크리스트가 고정 해지되었습니다</span>
        </div>,
      );
    } else {
      toast(
        <div className="checklist-toast">
          <TaskTickIcon />
          <span className="checklist-toast__text">체크리스트가 고정되었습니다</span>
        </div>,
      );
    }
  };

  React.useEffect(() => {
    fetchChecklist();
  }, [date]);

  const popupStyle = {
    borderRadius: 8,
    width: '102px',
    height: '84px',
    border: '1px solid #E9E9E9',
    boxShadow: '2px 2px 20px rgba(0, 0, 0, 0.06)',
  };
  const checkYetList = checklistData.filter((i) => !i.CS_SEQ);
  const unusualList: IChecklist[] = [];
  const completeList: IChecklist[] = [];
  checklistData.filter((i: IChecklist) => {
    let unusual = false;
    if (i.CS_SEQ) {
      i.CHECK_LIST?.split('@').map((check, _i) => {
        if (_i % 2 === 1 && check === '2') {
          console.log('_i', _i, check);
          unusual = true;
        }
      });
      if (unusual) {
        unusualList.push(i);
      } else {
        completeList.push(i);
      }
    }
  });

  return (
    <>
      <WithSideBarAndNavBar>
        <div className="checklist">
          <div className="checklist__head">
            <div className="checklist__head__text">체크리스트</div>
            <div className="checklist__head__button">
              <Button label="+추가하기" type="type2" action={() => enterUpdateChecklist()} height={50} />
            </div>
          </div>
          <div className="checklist__dates">
            <div className="checklist__arrows__left">
              <NavButton action={() => handleDateChange('left')} direction="LEFT" />
            </div>
            <div className="checklist__arrows__input">
              <DateSelector onChange={handleSelectDate} value={date} placeholder="종료일 선택" />
            </div>
            <div className="checklist__arrows__right">
              <NavButton action={() => handleDateChange('right')} direction="RIGHT" />
            </div>
            <div className="checklist__today-button">
              <Button label="오늘" type="type1" height={40} action={() => handleSelectDate(new Date())} />
            </div>
          </div>
          <div className="checklist__board">
            <div className="checklist__board__row">
              <div className="checklist__board__column-left">
                <div className="checklist__board__head">
                  미체크{' '}
                  <span className="checklist__board__span checklist__board__span-grey">{checkYetList.length}</span>
                </div>
                <div className="checklist__board__list">
                  {checkYetList.length > 0 && (
                    <>
                      {checkYetList.map((i) => {
                        return (
                          <div className="checklist__board__card" key={i.CHECK_SEQ}>
                            <div className="checklist__board__card__head">
                              <ToastContainer
                                position="top-center"
                                closeButton={false}
                                hideProgressBar={true}
                                toastStyle={{
                                  backgroundColor: '#292D31',
                                  borderRadius: '6px',
                                  width: '280px',
                                  height: '56px',
                                }}
                              />
                              <div className="checklist__board__card__texts" onClick={() => navigateToChecklistItem(i)}>
                                <div className="checklist__board__card__text">{i.TITLE}</div>
                                <div className="checklist__board__card__schedule">
                                  체크 예정시간 {i.END_TIME === '' ? '미지정' : ': ' + i.END_TIME}
                                </div>
                              </div>
                              <div className="checklist__board__card__action">
                                <Popup
                                  on="click"
                                  trigger={
                                    <div className="checklist__popup__icon">
                                      <ActionIcon />
                                    </div>
                                  }
                                  position="bottom center"
                                  style={popupStyle}
                                  basic
                                  flowing
                                  hoverable>
                                  <div className="checklist__popups">
                                    <div
                                      className="checklist__popup__resend-invite"
                                      onClick={() => {
                                        cardDropdown('ACCEPT', i);
                                      }}>
                                      수정
                                    </div>
                                    <div
                                      className="checklist__popup__delete-invite"
                                      onClick={() => {
                                        cardDropdown('REJECT', i);
                                      }}>
                                      삭제
                                    </div>
                                  </div>
                                </Popup>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                  {checklistData.length === 0 && (
                    <div className="checklist__board__card">
                      <span style={{ color: '#999' }}>등록된 체크리스트가 없습니다</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="checklist__board__column-center">
                <div className="checklist__board__head">
                  체크이상{' '}
                  <span className="checklist__board__span checklist__board__span-red">{unusualList.length}</span>
                </div>
                <div className="checklist__board__list">
                  {unusualList.length > 0
                    ? unusualList.map((i) => (
                        <div
                          className="checklist__board__card"
                          onClick={() => navigateToChecklistItem(i)}
                          key={i.CHECK_SEQ}>
                          <div className="checklist__board__card__head">
                            <ToastContainer
                              position="top-center"
                              closeButton={false}
                              hideProgressBar={true}
                              toastStyle={{
                                backgroundColor: '#292D31',
                                borderRadius: '6px',
                                width: '280px',
                                height: '56px',
                              }}
                            />
                            <div className="checklist__board__card__texts">
                              <div className="checklist__board__card__text">{i.TITLE}</div>
                              <div className="checklist__board__card__schedule">
                                체크 예정시간 {i.END_TIME === '' ? '미지정' : ': ' + i.END_TIME}
                              </div>
                            </div>
                            <div className="checklist__board__card__action">
                              <Popup
                                on="click"
                                trigger={
                                  <div className="checklist__popup__icon">
                                    <ActionIcon />
                                  </div>
                                }
                                position="bottom center"
                                style={popupStyle}
                                basic
                                flowing
                                hoverable>
                                <div className="checklist__popups">
                                  <div
                                    className="checklist__popup__resend-invite"
                                    onClick={() => {
                                      cardDropdown('ACCEPT', i);
                                    }}>
                                    수정
                                  </div>
                                  <div
                                    className="checklist__popup__delete-invite"
                                    onClick={() => {
                                      cardDropdown('REJECT', i);
                                    }}>
                                    삭제
                                  </div>
                                </div>
                              </Popup>
                            </div>
                          </div>
                        </div>
                      ))
                    : null}
                </div>
              </div>
              <div className="checklist__board__column-right">
                <div className="checklist__board__head">
                  완료{' '}
                  <span className="checklist__board__span checklist__board__span-green">{completeList.length}</span>
                </div>
                <div className="checklist__board__list">
                  {completeList
                    ? completeList.map((i) => (
                        <div className="checklist__board__card" key={i.CHECK_SEQ}>
                          <div className="checklist__board__card__head">
                            <ToastContainer
                              position="top-center"
                              closeButton={false}
                              hideProgressBar={true}
                              toastStyle={{
                                backgroundColor: '#292D31',
                                borderRadius: '6px',
                                width: '280px',
                                height: '56px',
                              }}
                            />
                            <div className="checklist__board__card__texts" onClick={() => navigateToChecklistItem(i)}>
                              <div className="checklist__board__card__text">{i.TITLE}</div>
                              <div className="checklist__board__card__schedule">
                                체크 예정시간 {i.END_TIME === '' ? '미지정' : ': ' + i.END_TIME}
                              </div>
                            </div>
                            <div className="checklist__board__card__action">
                              <Popup
                                on="click"
                                trigger={
                                  <div className="checklist__popup__icon">
                                    <ActionIcon />
                                  </div>
                                }
                                position="bottom center"
                                style={popupStyle}
                                basic
                                flowing
                                hoverable>
                                <div className="checklist__popups">
                                  <div
                                    className="checklist__popup__resend-invite"
                                    onClick={() => {
                                      cardDropdown('ACCEPT', i);
                                    }}>
                                    수정
                                  </div>
                                  <div
                                    className="checklist__popup__delete-invite"
                                    onClick={() => {
                                      cardDropdown('REJECT', i);
                                    }}>
                                    삭제
                                  </div>
                                </div>
                              </Popup>
                            </div>
                          </div>
                        </div>
                      ))
                    : null}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* {addChecklistModal && <AddChecklistModal onClose={handleToggleChecklistModal} />} */}
      </WithSideBarAndNavBar>
      {additionalCompletionModal && <AdditionalCompletionModal onClose={handleAdditionalCompletionModal} />}
    </>
  );
};
export default CheckList;
